import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaVideo, FaPhone, FaPhoneSlash, FaPlus, FaCalendarAlt, FaRecordVinyl, FaTrash } from 'react-icons/fa';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import BottomNav from '../components/BottomNav';
import { useApp, useAutoRefresh } from '../context/AppContext';
import { getTelemedicineSessions, getTelemedicineStats, createTelemedicineSession, updateTelemedicineSession, deleteTelemedicineSession, getPatients, getDoctors, uploadTelemedicineRecording, getTelemedicineJwt, getTelemedicineRecordingBlob } from '../services/api';

const EMPTY_FORM = { patientName: '', patientId: '', doctorId: '', doctorName: '', scheduled_at: '', type: 'Video', notes: '', status: 'Scheduled' };

// API returns scheduled_at as a bare "YYYY-MM-DD HH:mm:ss" IST wall-clock
// string (no timezone tag). A plain `new Date(...)` would parse it in the
// viewer's own browser timezone, which only happens to match if that
// browser is set to IST. Anchor explicitly to +05:30 so the join-window
// check below is correct regardless of viewer timezone.
const parseIstDate = (raw) => {
  if (!raw) return null;
  const iso = String(raw).replace(' ', 'T');
  const hasOffset = /[Zz]|[+-]\d\d:\d\d$/.test(iso);
  return new Date(hasOffset ? iso : `${iso}+05:30`);
};

// Mirrors the backend's getJwt rule: Cancelled is always closed; otherwise
// the session stays joinable for 3hr after it actually ended (end_time),
// falling back to scheduled_at before the first call has ended — including
// a rejoin after an earlier call already completed (admin joins later, or
// doctor/patient reconnect). No scheduled_at/end_time (legacy/manual
// session) falls back to the original "only while Scheduled" rule.
const isSessionJoinable = (session) => {
  if (session.status === 'Cancelled') return false;
  const windowAnchor = session.end_time || session.scheduled_at;
  if (windowAnchor) {
    return Date.now() <= parseIstDate(windowAnchor).getTime() + (3 * 60 * 60 * 1000);
  }
  return session.status === 'Scheduled';
};

const Telemedicine = () => {
  const { showToast, user } = useApp();
  const role = user?.role || 'Doctor';
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState({ total: 0, today: 0, completed: 0, scheduled: 0 });
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inCall, setInCall] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploadingRec, setUploadingRec] = useState(false);
  // Recordings are behind auth now — loaded on demand as blob: URLs, keyed by session id
  const [recordingUrls, setRecordingUrls] = useState({});
  const [loadingRecId, setLoadingRecId] = useState(null);

  // MediaRecorder refs — survive re-renders without causing them
  const mediaRecorderRef  = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordingStreamRef = useRef(null);

  // Jitsi IFrame API refs
  const jitsiApiRef       = useRef(null);
  const jitsiContainerRef = useRef(null);
  const jitsiInitializingRef = useRef(false); // guards against a duplicate init (e.g. React StrictMode double-effect)
  // Keep endCall stable inside the Jitsi event listener
  const endCallRef        = useRef(null);

  // Revoke every blob: URL we created for recordings when the page unmounts
  useEffect(() => () => {
    Object.values(recordingUrls).forEach(url => URL.revokeObjectURL(url));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // key = sessionId, or `${sessionId}:${filename}` for a specific past
  // recording when a session has more than one (rejoin within the 3hr window).
  const loadRecording = async (sessionId, filename) => {
    const key = filename ? `${sessionId}:${filename}` : sessionId;
    if (recordingUrls[key] || loadingRecId === key) return;
    setLoadingRecId(key);
    try {
      const res = await getTelemedicineRecordingBlob(sessionId, filename);
      const url = URL.createObjectURL(res.data);
      setRecordingUrls(prev => ({ ...prev, [key]: url }));
    } catch {
      showToast('Unable to load recording', 'error');
    }
    setLoadingRecId(null);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sessRes, statsRes, patRes, docRes] = await Promise.allSettled([
        getTelemedicineSessions(),
        getTelemedicineStats(),
        // Doctor: their assigned patients; Admin: all registered patients
        (role === 'Doctor' || role === 'Admin') ? getPatients() : Promise.resolve({ data: { data: [] } }),
        role === 'Admin' ? getDoctors() : Promise.resolve({ data: { data: [] } }),
      ]);
      if (sessRes.status === 'fulfilled') setSessions(sessRes.value.data?.data || []);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data?.data || {});
      if (patRes.status === 'fulfilled') setPatients(patRes.value.data?.data || []);
      if (docRes.status === 'fulfilled') setDoctors(docRes.value.data?.data || []);
    } catch { setSessions([]); }
    setLoading(false);
  }, [role]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useAutoRefresh(fetchData, 'telemedicine');

  useEffect(() => {
    if (!inCall) return;
    const interval = setInterval(() => setCallDuration(d => d + 1), 1000);
    return () => clearInterval(interval);
  }, [inCall]);

  // Always keep the ref pointing at the latest endCall so the Jitsi
  // "readyToClose" listener never holds a stale closure.
  useEffect(() => { endCallRef.current = endCall; });

  // ── Jitsi IFrame API — mount when call starts, dispose on unmount ──────────
  useEffect(() => {
    if (!inCall || !activeCall) return;
    if (jitsiInitializingRef.current) return; // prevent a duplicate init racing in
    jitsiInitializingRef.current = true;

    let disposed = false;

    const initJitsi = async () => {
      try {
        // 1. Fetch a signed JWT from the backend (fails with a clear message if
        //    unauthorized, the session isn't joinable, or JaaS isn't configured)
        const { data } = await getTelemedicineJwt(activeCall.id);
        const { token, roomName, domain } = data.data;
        if (disposed) return;

        // 2. Load the Jitsi external-API script (once)
        if (!window.JitsiMeetExternalAPI) {
          await new Promise((resolve, reject) => {
            const s = document.createElement('script');
            s.src = `https://${domain}/external_api.js`;
            s.onload  = resolve;
            s.onerror = () => reject(new Error(`Failed to load ${s.src}`));
            document.head.appendChild(s);
          });
        }
        if (disposed) return;

        // 3. Initialise with JWT + user info
        const api = new window.JitsiMeetExternalAPI(domain, {
          roomName,
          parentNode: jitsiContainerRef.current,
          jwt: token,
          configOverwrite: {
            prejoinPageEnabled: false,
            startAudioOnly:      activeCall.type === 'Audio',
            startWithVideoMuted: activeCall.type === 'Audio',
          },
          interfaceConfigOverwrite: {
            SHOW_JITSI_WATERMARK: false,
            TOOLBAR_BUTTONS: [
              'microphone', 'camera', 'hangup',
              'fullscreen', 'settings', 'raisehand',
            ],
          },
          userInfo: { displayName: user?.name || 'User' },
        });

        jitsiApiRef.current = api;

        // End call automatically when the user leaves via Jitsi's own button
        api.addEventListener('readyToClose', () => {
          if (endCallRef.current) endCallRef.current();
        });
        // Conference-level failures (network drop, kicked, server error) — end
        // the call cleanly instead of leaving a dead iframe on screen.
        api.addEventListener('videoConferenceLeft', () => {
          if (endCallRef.current) endCallRef.current();
        });
        api.addEventListener('connectionFailed', () => {
          showToast('Connection lost. Ending call.', 'error');
          if (endCallRef.current) endCallRef.current();
        });

      } catch (err) {
        // The backend already returns a safe, specific message for every
        // denial case (unauthorized / not joinable / misconfigured) — the
        // interceptor unwraps it to err.message, so just surface it.
        if (!disposed) {
          showToast(err.message || 'Unable to start video call', 'error');
          setInCall(false);
          setActiveCall(null);
        }
      }
    };

    initJitsi();

    return () => {
      disposed = true;
      jitsiInitializingRef.current = false;
      if (jitsiApiRef.current) {
        jitsiApiRef.current.dispose();
        jitsiApiRef.current = null;
      }
    };
  }, [inCall, activeCall]); // eslint-disable-line react-hooks/exhaustive-deps

  const startCall = async (session) => {
    setActiveCall(session);
    setInCall(true);
    setCallDuration(0);
    updateTelemedicineSession(session.id, { status: 'Active', start_time: new Date() }).catch(() => {});

    // Start local recording via MediaRecorder
    try {
      const constraints = session.type === 'Video'
        ? { audio: true, video: true }
        : { audio: true, video: false };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      recordingStreamRef.current = stream;
      recordedChunksRef.current = [];

      const mimeType = ['video/webm;codecs=vp9,opus', 'video/webm', 'audio/webm']
        .find(t => MediaRecorder.isTypeSupported(t)) || '';
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      recorder.ondataavailable = (e) => { if (e.data.size > 0) recordedChunksRef.current.push(e.data); };
      recorder.start(1000); // chunk every second
      mediaRecorderRef.current = recorder;
    } catch (err) {
      console.warn('Recording unavailable:', err.message);
    }

    showToast(`Starting ${session.type} call...`, 'success');
  };

  const endCall = async () => {
    const callSession = activeCall;
    const dur = Math.round(callDuration / 60);

    // Tear down call UI immediately so user isn't stuck
    setInCall(false);
    setActiveCall(null);
    setCallDuration(0);

    // Stop tracks first so the Jitsi iframe releases camera/mic
    if (recordingStreamRef.current) {
      recordingStreamRef.current.getTracks().forEach(t => t.stop());
      recordingStreamRef.current = null;
    }

    // Collect recorder blob, then upload
    let blob = null;
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      blob = await new Promise((resolve) => {
        mediaRecorderRef.current.onstop = () => {
          const b = new Blob(recordedChunksRef.current, { type: 'video/webm' });
          resolve(b.size > 100 ? b : null);
        };
        mediaRecorderRef.current.stop();
      });
      mediaRecorderRef.current = null;
      recordedChunksRef.current = [];
    }

    if (callSession) {
      await updateTelemedicineSession(callSession.id, { status: 'Completed', end_time: new Date(), duration: dur }).catch(() => {});

      if (blob) {
        setUploadingRec(true);
        try {
          const fd = new FormData();
          fd.append('recording', blob, `session-${callSession.id}-${Date.now()}.webm`);
          await uploadTelemedicineRecording(callSession.id, fd);
          showToast('Call ended · Recording saved', 'success');
        } catch {
          showToast('Call ended (recording upload failed)', 'error');
        }
        setUploadingRec(false);
      } else {
        showToast('Call ended');
      }
    }

    fetchData();
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (role === 'Doctor') { payload.doctorId = user.id; payload.doctorName = user.name; }
      if (role === 'Admin' && payload.doctorId) {
        const d = doctors.find(dc => dc.id === parseInt(payload.doctorId));
        if (d) payload.doctorName = d.name;
      }
      const res = await createTelemedicineSession(payload);
      setSessions(prev => [res.data?.data, ...prev]);
      showToast('Session scheduled!', 'success');
      setShowModal(false); setForm(EMPTY_FORM);
    } catch (err) { showToast(err.message || 'Failed to schedule session', 'error'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Cancel this session?')) return;
    try { await deleteTelemedicineSession(id); setSessions(prev => prev.filter(s => s.id !== id)); showToast('Session cancelled'); }
    catch (err) { showToast(err.message || 'Error', 'error'); }
  };

  const formatDuration = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div className="page-wrapper">
      <Header title="Telemedicine" />
      <Sidebar />
      <main className="page-content">
        <div className="page-header">
          <div><h1><FaVideo style={{ color: 'var(--primary)', marginRight: 8 }} />Telemedicine</h1><p>Video & audio consultations</p></div>
          {(role === 'Doctor' || role === 'Admin') && <button className="btn btn-primary" onClick={() => setShowModal(true)}><FaPlus /> Schedule</button>}
        </div>

        {inCall && activeCall ? (
          /* Real video/audio call using Jitsi Meet — works for both Video and Audio sessions */
          <div style={{ position: 'fixed', inset: 0, background: '#0d0d1a', zIndex: 1200, display: 'flex', flexDirection: 'column' }}>
            {/* Top bar */}
            <div style={{ padding: '10px 16px', background: '#1a1a2e', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div style={{ color: 'white', fontWeight: 700, fontSize: 15 }}>
                {activeCall.type === 'Video' ? <FaVideo style={{ marginRight: 8 }} /> : <FaPhone style={{ marginRight: 8 }} />}
                {role === 'Patient' ? `Dr. ${activeCall.doctorName || 'Doctor'}`
                  : role === 'Admin' ? `${activeCall.patientName || 'Patient'} ↔ Dr. ${activeCall.doctorName || 'Doctor'}`
                  : (activeCall.patientName || 'Patient')}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>{formatDuration(callDuration)}</span>
                {mediaRecorderRef.current && (
                  <span style={{ color: '#ef9a9a', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <FaRecordVinyl /> REC
                  </span>
                )}
              </div>
            </div>
            {/* Jitsi IFrame API container */}
            <div
              ref={jitsiContainerRef}
              style={{ flex: 1, width: '100%', minHeight: 0 }}
            />
            {/* End call bar */}
            <div style={{ padding: '14px 16px', background: '#1a1a2e', display: 'flex', justifyContent: 'center', gap: 20 }}>
              <button onClick={endCall} style={{ background: '#e53935', color: 'white', border: 'none', borderRadius: '50%', width: 60, height: 60, cursor: 'pointer', fontSize: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(229,57,53,0.5)' }} title="End Call">
                <FaPhoneSlash />
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="tele-stats-row">
              {[
                { icon: FaVideo, label: "Today's Calls", val: stats.today || 0, color: 'var(--primary)' },
                { icon: FaPhone, label: 'Total Sessions', val: stats.total || 0, color: '#2e7d32' },
                { icon: FaCalendarAlt, label: 'Scheduled', val: stats.scheduled || 0, color: '#1976d2' },
              ].map(s => (
                <div key={s.label} className="tele-stat card">
                  <s.icon style={{ color: s.color, fontSize: 24 }} />
                  <div className="tele-stat-num">{s.val}</div>
                  <div className="tele-stat-label">{s.label}</div>
                </div>
              ))}
            </div>

            <div className="section-title">
              {role === 'Patient' ? 'My Sessions' : 'Upcoming Consultations'}
            </div>

            {uploadingRec && (
              <div style={{ padding: '10px 14px', background: '#e3f2fd', borderRadius: 10, marginBottom: 10, fontSize: 13, color: '#1565c0', display: 'flex', alignItems: 'center', gap: 8 }}>
                <div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                Uploading recording, please wait…
              </div>
            )}

            {loading ? (
              <div className="loading-container"><div className="spinner" /></div>
            ) : sessions.length === 0 ? (
              <div className="empty-state"><FaVideo className="empty-state-icon" /><h3>No sessions found</h3></div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {sessions.map(session => (
                  <div key={session.id} className="card" style={{ padding: 14 }}>
                    {/* ── Session header row ── */}
                    <div className="tele-card">
                      <div className="tele-card-avatar">
                        {session.type === 'Video' ? <FaVideo size={20} /> : <FaPhone size={20} />}
                      </div>
                      <div className="tele-card-info">
                        <div className="tele-card-name">
                          {role === 'Patient' ? `Dr. ${session.doctorName || 'Doctor'}`
                            : role === 'Admin' ? `${session.patientName || 'Patient'} ↔ Dr. ${session.doctorName || 'Doctor'}`
                            : (session.patientName || 'Patient')}
                        </div>
                        <div className="tele-card-time">
                          {session.scheduled_at ? new Date(session.scheduled_at).toLocaleString('en-IN') : 'TBD'} · {session.type}
                          {session.duration ? ` · ${session.duration} min` : ''}
                        </div>
                        <span className={`status-badge status-${session.status?.toLowerCase()}`}>{session.status}</span>
                      </div>
                      <div className="tele-card-actions" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {isSessionJoinable(session) && (
                          <button className="btn btn-primary" style={{ padding: '7px 12px', fontSize: 13 }} onClick={() => startCall(session)}>
                            {session.type === 'Video' ? <FaVideo /> : <FaPhone />} Join
                          </button>
                        )}
                        {(role === 'Doctor' || role === 'Admin') && (
                          <button className="action-btn action-btn-delete" onClick={() => handleDelete(session.id)} title="Cancel session">
                            <FaTrash />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* ── Recording playback — fetched as an authenticated blob, never a public URL.
                         One entry per join (rejoin within the 3hr window records separately). ── */}
                    {(() => {
                      const recs = Array.isArray(session.recordings) && session.recordings.length > 0
                        ? session.recordings
                        : (session.recording_reference ? [{ url: session.recording_reference }] : []);
                      if (recs.length === 0) return null;
                      return (
                        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#2e7d32', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
                            <FaRecordVinyl /> Session Recording{recs.length > 1 ? `s (${recs.length})` : ''}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {recs.map((rec, i) => {
                              const filename = rec.url ? rec.url.split('/').pop() : null;
                              const isLatest = filename === (session.recording_reference || '').split('/').pop();
                              const key = recs.length > 1 && !isLatest ? `${session.id}:${filename}` : session.id;
                              const fetchFile = recs.length > 1 && !isLatest ? filename : undefined;
                              const participant = rec.uploadedBy === session.doctorId ? 'Doctor'
                                : rec.uploadedBy === session.patientId ? 'Patient'
                                : rec.uploadedBy ? 'Admin' : null;
                              return (
                                <div key={key}>
                                  {recs.length > 1 && (
                                    <div style={{ fontSize: 11, color: 'var(--text-light)', marginBottom: 4 }}>
                                      Recording {i + 1}{rec.uploadedAt ? ` · ${new Date(rec.uploadedAt).toLocaleString('en-IN')}` : ''}{participant ? ` · ${participant}` : ''}
                                    </div>
                                  )}
                                  {recordingUrls[key] ? (
                                    <>
                                      {session.type === 'Video' ? (
                                        <video src={recordingUrls[key]} controls style={{ width: '100%', maxHeight: 220, borderRadius: 8, background: '#000' }} />
                                      ) : (
                                        <audio src={recordingUrls[key]} controls style={{ width: '100%' }} />
                                      )}
                                      <a href={recordingUrls[key]} download={`session-${session.id}-recording-${i + 1}`} style={{ display: 'inline-block', marginTop: 6, fontSize: 12, color: 'var(--primary)' }}>
                                        Download recording
                                      </a>
                                    </>
                                  ) : (
                                    <button className="btn btn-outline" style={{ padding: '6px 12px', fontSize: 12 }} disabled={loadingRecId === key} onClick={() => loadRecording(session.id, fetchFile)}>
                                      {loadingRecId === key ? 'Loading…' : 'Load recording'}
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>
      <BottomNav />

      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-box">
            <div className="modal-header">
              <h2>Schedule Session</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreate}>
              {role === 'Admin' && doctors.length > 0 && (
                <div className="form-group">
                  <label>Doctor *</label>
                  <select required value={form.doctorId} onChange={e => setForm(p => ({ ...p, doctorId: e.target.value }))}>
                    <option value="">Select doctor...</option>
                    {doctors.map(d => <option key={d.id} value={d.id}>{d.name}{d.specialization ? ` — ${d.specialization}` : ''}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label>Patient *</label>
                {patients.length > 0 ? (
                  <select required value={form.patientId} onChange={e => {
                    const pObj = patients.find(pt => String(pt.userId) === e.target.value || String(pt.id) === e.target.value);
                    // Use userId so the patient's filter (patientId = req.user.id) matches
                    const pid = pObj?.userId ? String(pObj.userId) : e.target.value;
                    setForm(prev => ({ ...prev, patientId: pid, patientName: pObj ? pObj.name : '' }));
                  }}>
                    <option value="">Select patient...</option>
                    {patients.map(p => <option key={p.id} value={p.userId || p.id}>{p.name}</option>)}
                  </select>
                ) : (
                  <input required value={form.patientName} onChange={e => setForm(p => ({ ...p, patientName: e.target.value }))} placeholder="Patient name" />
                )}
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Date & Time *</label>
                  <input type="datetime-local" required value={form.scheduled_at} onChange={e => setForm(p => ({ ...p, scheduled_at: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Session Type</label>
                  <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}>
                    <option>Video</option><option>Audio</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Session notes..." />
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-light)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <FaRecordVinyl color="#e53935" /> All sessions are automatically recorded and saved for the doctor, patient, and admin.
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={saving}>
                  {saving ? 'Scheduling...' : 'Schedule Session'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .tele-stats-row { display: grid; grid-template-columns: repeat(3,1fr); gap: 10px; margin-bottom: 20px; }
        .tele-stat { display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 16px 10px; text-align: center; }
        .tele-stat-num { font-size: 22px; font-weight: 800; color: var(--text-dark); }
        .tele-stat-label { font-size: 11px; color: var(--text-light); font-weight: 600; }
        .tele-card { display: flex; align-items: center; gap: 14px; padding: 14px; }
        .tele-card-avatar { width: 48px; height: 48px; background: linear-gradient(135deg,#E8500A,#FF6B2C); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 20px; font-weight: 700; flex-shrink: 0; }
        .tele-card-info { flex: 1; }
        .tele-card-name { font-size: 15px; font-weight: 700; }
        .tele-card-time { font-size: 12px; color: var(--text-light); margin: 3px 0 6px; }
        .tele-card-actions { flex-shrink: 0; }
        .video-call-container { padding: 0; overflow: hidden; }
        .video-main { background: #1a1a2e; height: 300px; position: relative; display: flex; align-items: center; justify-content: center; }
        .video-placeholder { display: flex; flex-direction: column; align-items: center; gap: 12px; }
        .video-avatar { width: 80px; height: 80px; background: linear-gradient(135deg,#E8500A,#FF6B2C); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 32px; font-weight: 700; }
        .video-patient-name { color: white; font-size: 18px; font-weight: 700; }
        .video-status { color: rgba(255,255,255,0.7); font-size: 13px; display: flex; align-items: center; gap: 6px; }
        .video-self { position: absolute; bottom: 12px; right: 12px; width: 60px; height: 80px; background: #2d2d4e; border-radius: 10px; display: flex; align-items: center; justify-content: center; border: 2px solid rgba(255,255,255,0.2); }
        .call-controls { display: flex; align-items: center; justify-content: center; gap: 20px; padding: 20px; }
        .call-ctrl-btn { width: 54px; height: 54px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 20px; border: none; cursor: pointer; background: var(--primary-pale); color: var(--primary); transition: all 0.2s; }
        .call-ctrl-btn:hover { transform: scale(1.1); }
        .call-ctrl-btn.inactive { background: #e0e0e0; color: var(--text-light); }
        .call-ctrl-btn.end-call { background: #e53935; color: white; width: 62px; height: 62px; font-size: 22px; box-shadow: 0 4px 16px rgba(229,57,53,0.4); }
      `}</style>
    </div>
  );
};

export default Telemedicine;
